import React from "react";
import { Routes, Route } from "react-router-dom";
import { routeConfig } from "../config/routeConfig";
import { ProtectedRoute, AuthRedirect, AuthenticatedLayout } from "./index";
import { DashboardGuard, OnboardingGuard, GlobalAdminGuard } from "./guards/OnboardingGuards";
import { ROUTE_CATEGORIES } from "../constants/Paths";

/**
 * AppRoutes - Centralized route rendering component
 * 
 * This component takes the route configuration and renders all routes
 * with appropriate wrappers (authentication, layout, etc.) based on
 * the route configuration properties.
 * 
 * Benefits:
 * - Centralized route management
 * - Consistent route wrapping logic
 * - Easy to add new routes
 * - Clear separation of concerns
 */
export default function AppRoutes() {

	/**
 * Render a single route with appropriate wrappers
 */
	const renderRoute = (route) => {
		const { path, element: Component, requiresAuth, requiresLayout, redirectIfAuthenticated, navigateTo, category, requiresOnboardingGuard } = route;

		let routeElement;

		// Handle redirect routes
		if (category === 'redirect' && navigateTo) {
			routeElement = <Component to={navigateTo} replace />;
		} else {
			// Regular component routes
			routeElement = <Component />;

			// Add onboarding guard wrapper if required
			if (requiresOnboardingGuard) {
				// Use OnboardingGuard for onboarding routes, DashboardGuard for protected routes
				if (category === ROUTE_CATEGORIES.ONBOARDING) {
					routeElement = <OnboardingGuard>{routeElement}</OnboardingGuard>;
				} else {
					routeElement = <DashboardGuard>{routeElement}</DashboardGuard>;
				}
			}

			// Add global admin guard for admin routes
			if (category === ROUTE_CATEGORIES.ADMIN) {
				routeElement = <GlobalAdminGuard>{routeElement}</GlobalAdminGuard>;
			}

			// Add layout wrapper if required
			if (requiresLayout) {
				routeElement = <AuthenticatedLayout>{routeElement}</AuthenticatedLayout>;
			}

			// Add authentication wrapper if required
			if (requiresAuth) {
				routeElement = <ProtectedRoute>{routeElement}</ProtectedRoute>;
			}

			// Add redirect wrapper for authenticated users on public routes
			if (redirectIfAuthenticated) {
				routeElement = <AuthRedirect>{routeElement}</AuthRedirect>;
			}
		}

		return (
			<Route
				key={path}
				path={path}
				element={routeElement}
			/>
		);
	};

	return (
		<Routes>
			{/* Render public routes */}
			{routeConfig.public.map(renderRoute)}

			{/* Render onboarding routes */}
			{routeConfig.onboarding.map(renderRoute)}

			{/* Render protected routes */}
			{routeConfig.protected.map(renderRoute)}

			{/* Render dynamic routes */}
			{routeConfig.dynamic.map(renderRoute)}

			{/* Render redirect routes */}
			{routeConfig.redirects.map(renderRoute)}
		</Routes>
	);
} 